/**
 * ServiceAuditTab — AD Audit tab for ServiceForm
 * Displayed only when service_type === 'active_directory' and the record is saved.
 *
 * Sections:
 *   1. Audit configuration (thresholds, interval, enabled toggle)
 *   2. Last collected reports per type (expandable tables)
 *   3. "Actualizar ahora" button (triggers ad_audit command via AgentCommand API)
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Cog6ToothIcon,
  UserGroupIcon,
  ComputerDesktopIcon,
  NoSymbolIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

// ─── helpers ────────────────────────────────────────────────────────────────

function relativeTime(isoStr) {
  if (!isoStr) return '—';
  const diff = Date.now() - new Date(isoStr).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1)  return 'hace un momento';
  if (m < 60) return `hace ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `hace ${h} h`;
  return `hace ${Math.round(h / 24)} días`;
}

function Badge({ count, color = '#64748b' }) {
  if (count == null) return null;
  return (
    <span style={{
      background: count > 0 ? '#fee2e2' : '#dcfce7',
      color:      count > 0 ? '#991b1b' : '#15803d',
      borderRadius: 12, padding: '1px 8px', fontSize: '0.72rem', fontWeight: 700,
      marginLeft: 6,
    }}>
      {count}
    </span>
  );
}

function Section({ icon: Icon, title, count, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%',
          padding: '10px 14px', background: 'var(--bg-toolbar)', borderRadius: open ? '8px 8px 0 0' : 8,
          border: 'none', cursor: 'pointer', textAlign: 'left', color: 'var(--text-primary)',
        }}
      >
        {open ? <ChevronDownIcon style={{ width: 16, height: 16 }} /> : <ChevronRightIcon style={{ width: 16, height: 16 }} />}
        <Icon style={{ width: 18, height: 18, color: 'var(--text-secondary)' }} />
        <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{title}</span>
        <Badge count={count} />
        {count == null && <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 4 }}>sin datos</span>}
      </button>
      {open && <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)' }}>{children}</div>}
    </div>
  );
}

function ReportTable({ data, columns }) {
  if (!data || data.length === 0) {
    return <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: 4 }}>No se encontraron registros.</p>;
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c.key} style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.slice(0, 200).map((row, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
              {columns.map(c => (
                <td key={c.key} style={{ padding: '4px 8px', whiteSpace: 'nowrap', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {data.length > 200 && (
        <p style={{ fontSize: '0.75rem', color: '#94a3b8', padding: '4px 8px' }}>
          Mostrando 200 de {data.length} registros.
        </p>
      )}
    </div>
  );
}

// Column definitions
const USER_COLUMNS = [
  { key: 'samAccountName', label: 'Usuario' },
  { key: 'displayName',    label: 'Nombre completo' },
  { key: 'email',          label: 'Email' },
  { key: 'daysSinceLogon', label: 'Días inactivo', render: v => v != null ? `${v} días` : '—' },
  { key: 'lastLogon',      label: 'Último acceso', render: v => v ? new Date(v).toLocaleDateString('es-ES') : '—' },
];

const COMPUTER_COLUMNS = [
  { key: 'name',            label: 'Nombre' },
  { key: 'dnsHostName',     label: 'DNS Hostname' },
  { key: 'operatingSystem', label: 'SO' },
  { key: 'daysSinceLogon',  label: 'Días inactivo', render: v => v != null ? `${v} días` : '—' },
  { key: 'lastLogon',       label: 'Último acceso', render: v => v ? new Date(v).toLocaleDateString('es-ES') : '—' },
];

const DISABLED_COLUMNS = [
  { key: 'samAccountName', label: 'Usuario' },
  { key: 'displayName',    label: 'Nombre completo' },
  { key: 'email',          label: 'Email' },
  { key: 'whenChanged',    label: 'Modificado', render: v => v ? new Date(v).toLocaleDateString('es-ES') : '—' },
];

// ─── main component ──────────────────────────────────────────────────────────

export default function ServiceAuditTab({ serviceId, companyId }) {
  const [config,    setConfig]    = useState(null);
  const [reports,   setReports]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');
  const [savedOk,   setSavedOk]   = useState(false);
  const [localCfg,  setLocalCfg]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cfgRes, repRes] = await Promise.all([
        api.getServiceAuditConfig(serviceId, companyId),
        api.getServiceAuditLatest(serviceId, companyId),
      ]);
      setConfig(cfgRes.config);
      setLocalCfg(cfgRes.config);
      setReports(repRes.reports);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [serviceId, companyId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    setSavedOk(false);
    try {
      const res = await api.updateServiceAuditConfig(serviceId, localCfg, companyId);
      setConfig(res.config);
      setLocalCfg(res.config);
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const isDirtyConfig = localCfg && config && JSON.stringify(localCfg) !== JSON.stringify(config);

  if (loading) {
    return <p style={{ padding: 24, color: '#94a3b8' }}>Cargando datos de auditoría…</p>;
  }

  return (
    <div style={{ padding: '16px 0' }}>
      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: '0.82rem' }}>
          ⚠ {error}
        </div>
      )}

      {/* ── Config section ── */}
      <div style={{ background: 'var(--bg-toolbar)', border: '1px solid var(--border)', borderRadius: 8, padding: '14px 16px', marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Cog6ToothIcon style={{ width: 18, height: 18, color: '#64748b' }} />
          <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Configuración de auditoría</span>
          {savedOk && <span style={{ color: '#16a34a', fontSize: '0.78rem', marginLeft: 8 }}>✓ Guardado</span>}
        </div>

        {localCfg && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem' }}>
              <span style={{ color: '#64748b' }}>Auditoría activa</span>
              <select
                value={localCfg.enabled ? 'true' : 'false'}
                onChange={e => setLocalCfg(c => ({ ...c, enabled: e.target.value === 'true' }))}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
              >
                <option value="true">Activada</option>
                <option value="false">Desactivada</option>
              </select>
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem' }}>
              <span style={{ color: '#64748b' }}>Intervalo (min)</span>
              <input
                type="number" min={5} max={1440}
                value={localCfg.interval_minutes}
                onChange={e => setLocalCfg(c => ({ ...c, interval_minutes: parseInt(e.target.value) || 60 }))}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem' }}>
              <span style={{ color: '#64748b' }}>Umbral usuarios inactivos (días)</span>
              <input
                type="number" min={1}
                value={localCfg.inactive_user_days}
                onChange={e => setLocalCfg(c => ({ ...c, inactive_user_days: parseInt(e.target.value) || 90 }))}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem' }}>
              <span style={{ color: '#64748b' }}>Umbral equipos inactivos (días)</span>
              <input
                type="number" min={1}
                value={localCfg.inactive_computer_days}
                onChange={e => setLocalCfg(c => ({ ...c, inactive_computer_days: parseInt(e.target.value) || 60 }))}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
              />
            </label>

            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.82rem' }}>
              <span style={{ color: '#64748b' }}>Cuentas deshabilitadas</span>
              <select
                value={localCfg.check_disabled_accounts ? 'true' : 'false'}
                onChange={e => setLocalCfg(c => ({ ...c, check_disabled_accounts: e.target.value === 'true' }))}
                style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.82rem' }}
              >
                <option value="true">Activado</option>
                <option value="false">Desactivado</option>
              </select>
            </label>
          </div>
        )}

        {isDirtyConfig && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '5px 14px', borderRadius: 6, border: 'none',
                background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: '0.8rem',
                cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Guardando…' : 'Guardar configuración'}
            </button>
            <button
              type="button"
              onClick={() => setLocalCfg(config)}
              style={{ padding: '5px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-toolbar)', color: 'var(--text-primary)', fontSize: '0.8rem', cursor: 'pointer' }}
            >
              Cancelar
            </button>
          </div>
        )}
      </div>

      {/* ── Reports ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>Último reporte</span>
        <button
          type="button"
          onClick={load}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '5px 12px', borderRadius: 6, border: '1px solid var(--border)',
            background: 'var(--bg-toolbar)', color: 'var(--text-primary)', fontSize: '0.8rem', cursor: 'pointer',
          }}
        >
          <ArrowPathIcon style={{ width: 14, height: 14 }} />
          Actualizar
        </button>
      </div>

      {reports ? (
        <>
          <Section
            icon={UserGroupIcon}
            title={`Usuarios inactivos (>${localCfg?.inactive_user_days ?? 90} días)`}
            count={reports.inactive_users?.total_count ?? null}
          >
            {reports.inactive_users ? (
              <>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: 8 }}>
                  Recopilado {relativeTime(reports.inactive_users.collected_at)}
                </p>
                <ReportTable data={reports.inactive_users.data} columns={USER_COLUMNS} />
              </>
            ) : (
              <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Sin datos. Ejecuta el agente con el subcomando <code>audit</code>.</p>
            )}
          </Section>

          <Section
            icon={ComputerDesktopIcon}
            title={`Equipos inactivos (>${localCfg?.inactive_computer_days ?? 60} días)`}
            count={reports.inactive_computers?.total_count ?? null}
          >
            {reports.inactive_computers ? (
              <>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: 8 }}>
                  Recopilado {relativeTime(reports.inactive_computers.collected_at)}
                </p>
                <ReportTable data={reports.inactive_computers.data} columns={COMPUTER_COLUMNS} />
              </>
            ) : (
              <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Sin datos.</p>
            )}
          </Section>

          <Section
            icon={NoSymbolIcon}
            title="Cuentas deshabilitadas"
            count={reports.disabled_accounts?.total_count ?? null}
          >
            {reports.disabled_accounts ? (
              <>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: 8 }}>
                  Recopilado {relativeTime(reports.disabled_accounts.collected_at)}
                </p>
                <ReportTable data={reports.disabled_accounts.data} columns={DISABLED_COLUMNS} />
              </>
            ) : (
              <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Sin datos.</p>
            )}
          </Section>
        </>
      ) : (
        <p style={{ color: '#94a3b8', fontSize: '0.82rem' }}>
          No hay reportes de auditoría para este servicio. Asegúrate de que el agente esté configurado con <code>CMDB_AUDIT_SERVICE_ID={serviceId}</code> y ejecuta <code>python agent.py audit</code>.
        </p>
      )}
    </div>
  );
}
